import Bb from 'bluebird';
import qs from 'querystring';
import ConstantsStorage from '../../constants/index';
import URL_MAP from '../../constants/urls';
import {NotificationError, NotificationSuccess} from '../../services/notifications';
import {encode, getExpireTokenDate} from '../../services/common';
import UserApi from '../../api/user';
import {ActionSessionClear, ActionSessionStart, ActionSessionUpdate} from './actions-session';
import {
  ActionClearServerData, ActionGetUserFiles, ActionGetFavoriteContacts, ActionStoreServerData,
  ActionGetContacts, ActionGetRecentContacts, ActionGetUsersList,
} from './actions-request';
import {ActionRouteNavigate} from './actions-route';
import {ActionLoadingUpdate} from './actions-loading';
import {buildRoute, ROUTES} from '../../configs/routes';
import {ActionModalShowConfirmRegistration, ActionModalHide} from './actions-modal';
import {CookieStorageApi, localStorageApi} from '../../api/storage';
import {COOKIES_SESSION_STORE_KEY, COOKIES_USER_STORE_KEY} from '../../constants/cookies';

const Constants = ConstantsStorage();
const {USER_ACTIONS} = Constants;

export function ActionUserUpdate(data) {
  const encodedString = encode(data);
  localStorageApi.set(COOKIES_USER_STORE_KEY, encodedString);
  return {
    type: USER_ACTIONS.UPDATE,
    data,
  };
}

export function ActionUserClear() {
  return {
    type: USER_ACTIONS.CLEAR,
    data: {},
  };
}

export function ActionSucceedAuth(tokenData, canRedirect = false) {
  return (dispatch, getState) => {
    const sessionData = {};
    const token = tokenData.access_token || '';
    return UserApi.parseToken(token).then((userData) => {
      const state = getState();
      const tokenExpireDate = getExpireTokenDate(tokenData.expires_in);
      sessionData[Constants.USER_ACTIONS.LOGGED_IN] = true;
      sessionData.tokenData = tokenData;
      sessionData.tokenExpireDate = tokenExpireDate;
      dispatch(ActionUserUpdate(userData, tokenExpireDate));
      if (canRedirect) {
        const {redirect} = qs.parse(state.router.location.search.substring(1));
        if (redirect) {
          dispatch(ActionRouteNavigate(redirect));
        } else {
          dispatch(ActionRouteNavigate(buildRoute(URL_MAP.FEED)));
        }
      }
      dispatch(ActionSessionStart(sessionData, tokenExpireDate));
    }).catch((error) => {
      NotificationError(error.error_description || error.message);
    });
  };
}

export function ActionLogin(login, password) {
  return (dispatch) => {
    dispatch(ActionLoadingUpdate('loginLoading', true));
    return UserApi.login(login, password)
      .then((res) => {
        dispatch(ActionSucceedAuth(res, true));
      }).catch((error) => {
        NotificationError(error.error_description || error.message);
      }).finally(() => {
        dispatch(ActionLoadingUpdate('loginLoading', false));
      });
  };
}

export function ActionRegistration(data, redirectPath = ROUTES.LOGIN.route) {
  return (dispatch) => {
    dispatch(ActionLoadingUpdate('registrationLoading', true));
    UserApi.registration(data).then((res) => {
      dispatch(ActionModalShowConfirmRegistration({email: res.username, redirectPath}));
      if (window.ym) {
        if (data.role === 3) {
          window.ym(56333719, 'reachGoal', 'regcandidate');
        }
        if (data.role === 4) {
          window.ym(56333719, 'reachGoal', 'regempoyers');
        }
        if (data.role === 5) {
          window.ym(56333719, 'reachGoal', 'regagency');
        }
      }
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {
      dispatch(ActionLoadingUpdate('registrationLoading', false));
    });
  };
}

export function ActionChangePassword(data) {
  return (dispatch) => {
    dispatch(ActionLoadingUpdate('changePasswordLoading', true));

    UserApi.changePassword(data).then(() => {
      dispatch(ActionModalHide());
      NotificationSuccess('Пароль успешно изменен!');
    }).catch((error) => {
      NotificationError(error.error_description || error.message);
    }).finally(() => {
      dispatch(ActionLoadingUpdate('changePasswordLoading', false));
    });
  };
}

export function ActionSignOut(skipServerLogout) {
  return (dispatch) => {
    dispatch(ActionLoadingUpdate('signOutLoading', true));
    Bb.try(() => {
      if (skipServerLogout) {
        return Promise;
      }
      return UserApi.logout();
    })
      .catch((error) => {
        NotificationError(error.error_description || error.message);
      })
      .finally(() => {
        dispatch({type: USER_ACTIONS.SIGN_OUT});
        dispatch(ActionSessionClear());
        dispatch(ActionClearServerData());
        dispatch(ActionUserClear());
        dispatch(ActionRouteNavigate(ROUTES.LOGIN.route));
        dispatch(ActionLoadingUpdate('signOutLoading', false));
        localStorageApi.delete(COOKIES_USER_STORE_KEY);
        CookieStorageApi.delete(COOKIES_SESSION_STORE_KEY);
      });
  };
}

export function ActionGetUserInfo() {
  return (dispatch, getState) => {
    dispatch(ActionLoadingUpdate('userLoading', true));
    return UserApi.getUserInfo().then((res) => {
      const state = getState();
      const tokenExpireStringDate = state.rSession.tokenExpireDate;
      const expDate = tokenExpireStringDate ? new Date(tokenExpireStringDate) : null;
      dispatch(ActionUserUpdate(res, expDate));
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {
      dispatch(ActionLoadingUpdate('userLoading', false));
    });
  };
}

export function ActionUpdateTokenData() {
  return (dispatch, getState) => {
    const state = getState();
    const sessionData = state.rSession || {};
    const tokenData = sessionData.tokenData || {};
    const refreshToken = tokenData.refresh_token;
    if (refreshToken) {
      return UserApi.refreshToken(refreshToken).then((res) => {
        const _sessionData = {};
        const tokenExpireDate = getExpireTokenDate(res.expires_in);
        _sessionData[Constants.USER_ACTIONS.LOGGED_IN] = true;
        _sessionData.tokenData = res;
        _sessionData.tokenExpireDate = tokenExpireDate;
        dispatch(ActionSessionUpdate(_sessionData, tokenExpireDate));
        dispatch(ActionGetUserInfo());
      }).catch((error) => {
        NotificationError(error.message || error.error_description);
      }).finally(() => {});
    }
  };
}

export function ActionUpdateUser(data) {
  return (dispatch) => {
    dispatch(ActionLoadingUpdate('updateUserLoading', true));
    return UserApi.updateUser(data).then(() => {
      NotificationSuccess('Информация успешно обновленна');
      dispatch(ActionUpdateTokenData());
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {
      dispatch(ActionLoadingUpdate('updateUserLoading', false));
    });
  };
}

export function ActionRemoveUser(userId) {
  return (dispatch) => {
    dispatch(ActionLoadingUpdate('removeUserLoading', true));
    UserApi.removeUser(userId).then((res) => {
      NotificationSuccess('Пользователь успешно удален');
      dispatch(ActionSignOut());
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {
      dispatch(ActionLoadingUpdate('removeUserLoading', false));
    });
  };
}

export function ActionCheckVerificationToken(token) {
  return (dispatch) => UserApi.verifyEmail(token).then((res) => {
    console.log(res);
    dispatch(ActionSucceedAuth(res, true));
  }).catch((error) => {
    dispatch(ActionRouteNavigate(ROUTES.LOGIN.route));
    NotificationError(error.message || error.error_description);
  }).finally(() => {});
}

export function ActionRemoveUserFile(fileId) {
  return (dispatch) => {
    UserApi.removeUserFile(fileId).then(() => {
      NotificationSuccess('Файл успешно удален.');
      dispatch(ActionModalHide());
      dispatch(ActionGetUserFiles());
      dispatch(ActionGetUserInfo());
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {
    });
  };
}

export function ActionRegistrationFromAdminPanel(data, sendEmail) {
  return (dispatch) => {
    dispatch(ActionLoadingUpdate('registrationLoading', true));
    UserApi.registration(data, sendEmail).then((res) => {
      if (sendEmail) dispatch(ActionModalShowConfirmRegistration({email: res.username}));
      dispatch(ActionRouteNavigate(ROUTES.ADMIN_PANEL_CREATE_USER.route));
      NotificationSuccess('Пользователь успешно зарегистрирован');
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {
      dispatch(ActionLoadingUpdate('registrationLoading', false));
    });
  };
}

export function ActionUpdateUserList(newUser) {
  return (dispatch, getState) => {
    const state = getState().rServerData;
    const usersList = state.usersList || [];

    const newUsersList = usersList.map((item) => {
      if (newUser._id === item._id) {
        return newUser;
      }

      return item;
    });

    dispatch(ActionStoreServerData('usersList', newUsersList));
  };
}

export function ActionBanUser(userId) {
  return (dispatch) => {
    UserApi.banUser(userId).then((res) => {
      console.log(res);
      dispatch(ActionUpdateUserList(res));
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {

    });
  };
}

export function ActionUnlockUser(userId) {
  return (dispatch) => {
    UserApi.unlockUser(userId).then((res) => {
      console.log(res);
      dispatch(ActionUpdateUserList(res));
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {

    });
  };
}

export function ActionAdminUpdateUser(data, id) {
  return (dispatch, getState) => {
    const state = getState();
    const user = state.rUser || {};
    dispatch(ActionLoadingUpdate('updateUserLoading', true));
    return UserApi.updateUser(data, id).then(() => {
      const isCurrentUser = user._id === id;
      if (isCurrentUser) {
        dispatch(ActionGetUserInfo());
      }
      NotificationSuccess('Cохранено');
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {
      dispatch(ActionLoadingUpdate('updateUserLoading', false));
    });
  };
}

export function ActionRemoveContactFromFavorite(userId) {
  return (dispatch) => {
    UserApi.removeContactFromFavorite(userId).then(() => {
      dispatch(ActionGetFavoriteContacts());
      dispatch(ActionGetContacts());
      dispatch(ActionGetRecentContacts());
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {

    });
  };
}

export function ActionDeleteContact(userId) {
  return (dispatch) => {
    UserApi.removeContact(userId).then(() => {
      dispatch(ActionGetFavoriteContacts());
      dispatch(ActionGetContacts());
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {
    });
  };
}

export function ActionLoginWithMailRu(code, redirectUri) {
  return (dispatch) => {
    UserApi.loginWithSn({token: code, type: 'mailRu', redirectUri}).then((res) => {
      console.log(res);
      if (res.access_token) {
        dispatch(ActionSucceedAuth(res, true));
      }
    }).catch((error) => {
      dispatch(ActionRouteNavigate(ROUTES.LOGIN.route));
      NotificationError(error.message || error.error_description);
    }).finally(() => {

    });
  };
}

export function ActionLoginWithOk(token, secretKey) {
  return (dispatch) => {
    UserApi.loginWithSn({token, type: 'odnoklassniki', secretKey}).then((res) => {
      console.log(res);
      if (res.access_token) {
        dispatch(ActionSucceedAuth(res, true));
      }
    }).catch((error) => {
      dispatch(ActionRouteNavigate(ROUTES.LOGIN.route));
      NotificationError(error.message || error.error_description);
    }).finally(() => {

    });
  };
}

export function ActionLoginWithFb(token) {
  return (dispatch) => {
    UserApi.loginWithSn({token, type: 'facebook'}).then((res) => {
      console.log(res);
      if (res.access_token) {
        dispatch(ActionSucceedAuth(res, true));
      }
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
      dispatch(ActionRouteNavigate(ROUTES.LOGIN.route));
    }).finally(() => {

    });
  };
}

export function ActionLoginWithVk(code, redirectUri) {
  return (dispatch) => {
    UserApi.loginWithSn({token: code, type: 'vk', redirectUri}).then((res) => {
      console.log(res);
      if (res.access_token) {
        dispatch(ActionSucceedAuth(res, true));
      }
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
      dispatch(ActionRouteNavigate(ROUTES.LOGIN.route));
    }).finally(() => {

    });
  };
}

export function ActionLinkAccountWithSn(data) {
  return (dispatch) => {
    UserApi.linkAccountWithSn(data).then(() => {
      dispatch(ActionGetUserInfo());
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {
      dispatch(ActionRouteNavigate(URL_MAP.PROFILE_SETTINGS));
    });
  };
}

export function ActionUnLinkAccountWithSn(type) {
  return (dispatch) => {
    UserApi.unlinkAccountWithSn(type)
      .then(() => dispatch(ActionGetUserInfo()))
      .catch((error) => {
        NotificationError(error.message || error.error_description);
      });
  };
}

export function ActionUpdateUserNotificationSettings(data) {
  return (dispatch) => UserApi.updateUserSettings(data).then(() => {
    dispatch(ActionGetUserInfo());
  }).catch(() => {
  }).finally(() => {});
}

export function ActionAdminRemoveUser(userId) {
  return (dispatch, getState) => {
    const state = getState();
    const userData = state.rUser;
    const userDataId = userData._id;
    dispatch(ActionLoadingUpdate('removeUserLoading', true));
    return UserApi.removeUser(userId).then(() => {
      NotificationSuccess('Пользователь успешно удален');
      if (userId === userDataId) {
        dispatch(ActionSignOut());
      }
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {
      dispatch(ActionLoadingUpdate('removeUserLoading', false));
    });
  };
}

export function ActionUpdateDeveloperSettings(data, userId) {
  return (dispatch) => UserApi.updateDeveloperSettings(data, userId).then(() => {
    dispatch(ActionGetUserInfo());
  }).catch((error) => {
    NotificationError(error.message || error.error_description);
  }).finally(() => {
  });
}

export function ActionLoginFromOtherPage(login, password) {
  return (dispatch, getState) => {
    const state = getState();
    const loggedIn = state.rSession.LOGGED_IN;
    Bb.try(() => {
      if (loggedIn) {
        return UserApi.logout();
      }
      return Promise;
    }).then(() => {
      dispatch({type: USER_ACTIONS.SIGN_OUT});
      dispatch(ActionSessionClear());
      dispatch(ActionClearServerData());
      dispatch(ActionUserClear());
    }).catch((error) => {
      NotificationError(error.message || error.error_description);
    }).finally(() => {
      dispatch(ActionLogin(login, password));
    });
  };
}
